import axios from 'axios';
// Import types if needed (optional)
import { AiModel } from '../types';
import config from '../config';

// Create an Axios instance with a base URL from config
const api = axios.create({ baseURL: config.apiBaseUrl });

// Create a flag to track if a token refresh is in progress
let isRefreshingToken = false;
// Store pending requests that should be retried after token refresh
let pendingRequests: Array<() => void> = [];

/**
 * Sets up an Axios interceptor to attach the Authorization header
 * with the provided token on all outgoing requests.
 * @param token - The authentication token
 */
export const setupApiInterceptors = (token: string | null) => {
  // Clear previous interceptors to prevent duplicates
  api.interceptors.request.eject(0);
  api.interceptors.response.eject(0);
  
  // Request interceptor for adding auth token
  api.interceptors.request.use((config) => {
    // Always get the latest token from storage
    const currentToken = localStorage.getItem('token') || token;
    if (currentToken) {
      config.headers.Authorization = `Bearer ${currentToken}`;
    }
    return config;
  });
  
  // Response interceptor for handling token expiration/auth errors
  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      // If the response status is 401 (Unauthorized), we need to handle token issues
      const status = error.response?.status;
      const isUnauthorized = status === 401;
      const needsRefresh = error.response?.data?.refreshToken === true;
      const noTokenError = error.response?.data?.error === 'Unauthorized: No token provided';
      
      console.log('API Error:', error.response?.data, 'Status:', status);

      // Get the original request config
      const originalRequest = error.config;
      
      // Check if this is a login page or request
      const isLoginPage = window.location.pathname === '/login';
      const isLoginRequest = originalRequest?.url?.includes('/login');

      // Skip auth handling for login-related operations
      if (isLoginPage || isLoginRequest) {
        return Promise.reject(error);
      }

      // If we're getting a "no token provided" error but we have a token in localStorage,
      // this is likely a case where the token was added after the page loaded
      // Let's avoid redirecting and instead retry the request with the current token
      if (isUnauthorized && noTokenError && localStorage.getItem('token')) {
        console.log('Token exists in localStorage but not in request, retrying...');
        const token = localStorage.getItem('token');
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      }

      // Check if the error is an authentication error that needs token refresh
      if (isUnauthorized && needsRefresh && !originalRequest._retry) {
        // Mark this request as already tried for refresh
        originalRequest._retry = true;

        // If we're already refreshing, add this request to the queue
        if (isRefreshingToken) {
          return new Promise(resolve => {
            pendingRequests.push(() => {
              resolve(api(originalRequest));
            });
          });
        }

        // Set the refreshing flag
        isRefreshingToken = true;

        try {
          // Trigger a refresh via Google Auth
          const refreshEvent = new CustomEvent('token:refresh');
          window.dispatchEvent(refreshEvent);

          // Wait for the token to be refreshed (max 10 seconds)
          const tokenRefreshed = await new Promise<boolean>((resolve) => {
            const timeout = setTimeout(() => resolve(false), 10000);
            const listener = () => {
              clearTimeout(timeout);
              resolve(true);
            };
            window.addEventListener('token:refreshed', listener, { once: true });
          });

          // Reset refreshing flag
          isRefreshingToken = false;

          if (tokenRefreshed) {
            // Retry all pending requests
            pendingRequests.forEach(callback => callback());
            pendingRequests = [];

            // Retry the original request with the new token
            return api(originalRequest);
          } else {
            // Token refresh failed - redirect to login
            handleAuthFailure();
            return Promise.reject(error);
          }
        } catch (refreshError) {
          // Reset refreshing flag
          isRefreshingToken = false;
          
          // Handle auth failure
          handleAuthFailure();
          return Promise.reject(refreshError);
        }
      } else if (isUnauthorized) {
        // All other 401 errors - redirect to login
        handleAuthFailure();
      }
      
      return Promise.reject(error);
    }
  );
};

// Helper function to handle auth failures consistently
const handleAuthFailure = () => {
  // Clear user data from localStorage
  localStorage.removeItem('user');
  localStorage.removeItem('token');
  
  // Redirect to login page if not already there
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

// API functions for Trainer Server endpoints:

// Create a new AI
export const createAi = (data: Partial<AiModel>) => api.post('/trainer/ais', data);

// Get all AIs for the trainer
export const getAis = () => api.get('/trainer/ais');

// Update an existing AI
export const updateAi = (id: string, data: Partial<AiModel>) => api.put(`/trainer/ais/${id}`, data);

// Delete an AI
export const deleteAi = (id: string) => api.delete(`/trainer/ais/${id}`);

// Create a new collection for an AI (optionally from existing collection)
export const createCollection = (aiId: string, data: { fromCollectionId?: string, version: string }) => 
  api.post(`/trainer/ais/${aiId}/collections`, data);

// Add a Q&A pair to a collection
export const addQna = (aiId: string, collectionId: string, qna: { question: string, answer: string, tags?: string[] }) => 
  api.post(`/trainer/ais/${aiId}/collections/${collectionId}/qna`, qna);

// Update a collection's instructions, temperature, and Q&A pairs limit
export const updateCollectionInstructions = (aiId: string, collectionId: string, instruction: string, temperature?: number, qaPairsLimit?: number, debugOutline?: boolean) =>
  api.put(`/trainer/ais/${aiId}/collections/${collectionId}`, { instruction, temperature, qaPairsLimit, debugOutline });

// Update a collection's settings including color and chatbot title
export const updateCollectionSettings = (
  aiId: string,
  collectionId: string,
  settings: {
    instruction?: string;
    temperature?: number;
    qaPairsLimit?: number;
    debugOutline?: boolean;
    color?: string;
    chatbotTitle?: string;
    assistantName?: string;
    chatbotIcon?: string;
  }
) =>
  api.put(`/trainer/ais/${aiId}/collections/${collectionId}`, settings);

// Get Q&A pairs from a collection
export const getQna = (aiId: string, collectionId: string) => 
  api.get(`/trainer/ais/${aiId}/collections/${collectionId}/qna`);

// Update a Q&A pair in a collection
export const updateQna = (aiId: string, collectionId: string, qnaId: string, qna: { question: string, answer: string, tags?: string[] }) => 
  api.put(`/trainer/ais/${aiId}/collections/${collectionId}/qna/${qnaId}`, qna);

// Get a single Q&A pair by ID
export const getQnaPair = (aiId: string, collectionId: string, qnaId: string) => 
  api.get(`/trainer/ais/${aiId}/collections/${collectionId}/qna/${qnaId}`);

// Get Q&A pair status including edit history
export const getQnaPairStatus = (aiId: string, collectionId: string, qnaId: string, snapshotTimestamp?: string) => 
  api.get(`/trainer/ais/${aiId}/collections/${collectionId}/qna/${qnaId}/status${snapshotTimestamp ? `?snapshotTimestamp=${encodeURIComponent(snapshotTimestamp)}` : ''}`);

// Delete a Q&A pair from a collection
export const deleteQna = (aiId: string, collectionId: string, qnaId: string) => 
  api.delete(`/trainer/ais/${aiId}/collections/${collectionId}/qna/${qnaId}`);

// Delete all Q&A pairs from a collection (fetches all pairs first, then deletes them one by one)
export const deleteAllQna = async (aiId: string, collectionId: string) => {
  // First get all QnA pairs
  const response = await getQna(aiId, collectionId);
  const qnaPairs = response.data;
  
  // Then delete them one by one using Promise.all for parallel processing
  if (qnaPairs && qnaPairs.length > 0) {
    await Promise.all(
      qnaPairs.map((qna: { qnaId: string }) => deleteQna(aiId, collectionId, qna.qnaId))
    );
  }
  
  return { data: { deletedCount: qnaPairs.length } };
};

// Delete specific Q&A pairs by ID array (for filtered deletion)
export const deleteFilteredQna = async (aiId: string, collectionId: string, qnaIds: string[]) => {
  if (!qnaIds || qnaIds.length === 0) {
    return { data: { deletedCount: 0 } };
  }
  
  // Delete the specified Q&A pairs in parallel
  await Promise.all(
    qnaIds.map((qnaId: string) => deleteQna(aiId, collectionId, qnaId))
  );
  
  return { data: { deletedCount: qnaIds.length } };
};

// Import Q&A pairs from CSV data
export const importQnaFromCSV = (
  aiId: string, 
  collectionId: string, 
  csvData: Array<{ question: string; answer: string; tags?: string[] }>,
  filename: string,
  batchSize?: number
) => 
  api.post(`/trainer/ais/${aiId}/collections/${collectionId}/qna/import`, { 
    csvData, 
    filename,
    batchSize: batchSize || 10 
  });

// Export Q&A pairs as CSV with optional filters
export const exportQnaAsCSV = (
  aiId: string, 
  collectionId: string, 
  filters?: {
    textQuery?: string;
    tagQuery?: string;
    sourceFilter?: string;
  }
) => {
  const params = new URLSearchParams();
  if (filters?.textQuery) params.append('textQuery', filters.textQuery);
  if (filters?.tagQuery) params.append('tagQuery', filters.tagQuery);
  if (filters?.sourceFilter) params.append('sourceFilter', filters.sourceFilter);
  
  const queryString = params.toString();
  return api.get(`/trainer/ais/${aiId}/collections/${collectionId}/qna/export${queryString ? `?${queryString}` : ''}`, {
    responseType: 'blob'
  });
};

// Process webpage URLs to create Q&A pairs
export const processWebpageUrls = (aiId: string, collectionId: string, data: { url: string, qaPairsCount?: number, processImages?: boolean }) => 
  api.post(`/trainer/ais/${aiId}/collections/${collectionId}/process-webpages`, data);

// Delete all Q&A pairs associated with a specific source URL (changed from DELETE to POST)
export const deleteQnaBySource = (aiId: string, collectionId: string, data: { source: string }) => 
  api.post(`/trainer/ais/${aiId}/collections/${collectionId}/delete-by-source`, data);

// Process uploaded files (text or PDF) to create Q&A pairs
export const processFiles = (aiId: string, collectionId: string, files: File[], qaPairsCount: number) => {
  // Convert the files to base64 format
  const processFilesPromises = files.map(file => {
    return new Promise<{ name: string; content: string; type: string }>((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Extract base64 content from the DataURL (remove the prefix)
        const base64Content = reader.result?.toString().split(',')[1] || '';
        resolve({
          name: file.name,
          content: base64Content,
          type: file.type
        });
      };
      reader.onerror = error => reject(error);
    });
  });

  return Promise.all(processFilesPromises).then(fileData => {
    return api.post(`/trainer/ais/${aiId}/collections/${collectionId}/process-files`, { 
      files: fileData,
      qaPairsCount
    });
  });
};

// Publish a collection (make it public)
export const publishCollection = (aiId: string, collectionId: string) => 
  api.put(`/trainer/ais/${aiId}/collections/${collectionId}/publish`);

// Delete a collection
export const deleteCollection = (aiId: string, collectionId: string) =>
  api.delete(`/trainer/ais/${aiId}/collections/${collectionId}`);

// Update a collection's version name
export const updateCollectionVersion = (aiId: string, collectionId: string, versionName: string) => 
  api.put(`/trainer/ais/${aiId}/collections/${collectionId}`, { version: versionName });

// Toggle collection public/private status
export const toggleCollectionPublic = (aiId: string, collectionId: string, isPublic: boolean) =>
  api.put(`/trainer/ais/${aiId}/collections/${collectionId}/publish`, { isPublic });

// Get conversations for an AI (LEGACY - kept for backward compatibility)
export const getConversations = (aiId: string) => api.get(`/trainer/ais/${aiId}/conversations`);

// NEW UNIVERSAL CONVERSATION APIS
// Get conversations for an AI from universal logging system
export const getAiConversations = (aiId: string, limit: number = 50, offset: number = 0) => 
  api.get(`/api/review/ai/${aiId}/conversations?limit=${limit}&offset=${offset}`);

// Get conversation analytics for an AI
export const getAiConversationAnalytics = (aiId: string, days: number = 30) => 
  api.get(`/api/review/ai/${aiId}/analytics?days=${days}`);

// Get detailed conversation with all messages and context
export const getConversationDetails = (aiId: string, conversationId: string) => 
  api.get(`/api/review/ai/${aiId}/conversations/${conversationId}`);

// Update conversation annotation
export const updateConversationAnnotation = (aiId: string, conversationId: string, annotation: string) => 
  api.put(`/api/review/ai/${aiId}/conversations/${conversationId}/annotation`, { annotation });

// Search conversations by content
export const searchAiConversations = (aiId: string, query: string, limit: number = 20) => 
  api.get(`/api/review/ai/${aiId}/search?q=${encodeURIComponent(query)}&limit=${limit}`);

// VERSION-SPECIFIC CONVERSATION APIS
// Get conversations for a specific AI version/collection
export const getCollectionConversations = (aiId: string, collectionId: string, limit: number = 50, offset: number = 0) => 
  api.get(`/api/review/ai/${aiId}/versions/${collectionId}/conversations?limit=${limit}&offset=${offset}`);

// Get conversation analytics for a specific AI version/collection
export const getCollectionConversationAnalytics = (aiId: string, collectionId: string, days: number = 30) => 
  api.get(`/api/review/ai/${aiId}/versions/${collectionId}/analytics?days=${days}`);

// Get detailed conversation with all messages and context for a specific version
export const getCollectionConversationDetails = (aiId: string, collectionId: string, conversationId: string) => 
  api.get(`/api/review/ai/${aiId}/versions/${collectionId}/conversations/${conversationId}`);

// Search conversations by content for a specific AI version/collection
export const searchCollectionConversations = (aiId: string, collectionId: string, query: string, limit: number = 20) => 
  api.get(`/api/review/ai/${aiId}/versions/${collectionId}/search?q=${encodeURIComponent(query)}&limit=${limit}`);

// Chat endpoint (for end-user interactions)
export const chat = (data: { aiId: string, collectionId: string, userId: string, question: string }) => 
  api.post('/user/chat', data);

// Test chat endpoint (for debugging in the trainer UI)
export const testChat = (aiId: string, collectionId: string, question: string, sessionId?: string, conversationHistory?: any[]) => 
  api.post('/trainer/debug/chat', { aiId, collectionId, question, sessionId, conversationHistory });

// Demo chat endpoint (public access, no authentication)
// Backend fetches history from Supabase automatically
export const demoChat = (
  aiId: string,
  collectionId: string,
  question: string,
  sessionId?: string,
  conversationId?: string | null
) => {
  const payload: any = { aiId, collectionId, question, userId: `demo-${sessionId}`, sessionId };
  if (conversationId) {
    payload.conversationId = conversationId;
  }
  return api.post('/user/chat', payload);
};

// Smartview: upload a user window image (creates a conversation if needed)
export const uploadImageToConversation = (
  aiId: string,
  collectionId: string,
  image: string,
  userIdentifier: string,
  conversationId?: string | null
) => {
  const id = conversationId || 'new';
  return api.post(`/conversation/${id}/upload-image`, {
    image,
    aiId,
    collectionId,
    userIdentifier,
  });
};

// Fetch schema from external API (e.g., WooCommerce)
export const fetchTableSchema = (aiId: string, params: { 
  apiUrl: string;
  apiKey: string;
  apiSecret: string;
  tableTypes: string[];
  collectionId?: string;
  databaseSourceType?: string;
  rowLimit?: number;
}) => 
  api.post(`/trainer/ais/${aiId}/tables/schema${params.collectionId ? `?collectionId=${params.collectionId}` : ''}`, params);

// Get table data with pagination
export const getTableData = (aiId: string, tableId: string, page: number = 1, pageSize: number = 10) => 
  api.get(`/trainer/ais/${aiId}/tables/${tableId}/data`, { params: { page, pageSize } });

// Upload CSV file for preview
export const uploadCsvPreview = (aiId: string, file: File) => {
  const formData = new FormData();
  formData.append('csv', file);
  return api.post(`/trainer/ais/${aiId}/tables/csv-preview`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

// Create a new table
export const createTable = (aiId: string, data: { name: string, description: string, source: string, sourceConfig: any, schema: any, rowLimit?: number, sourceType?: string, originalFileName?: string, csvData?: any[], collectionId?: string | null }) => 
  api.post(`/trainer/ais/${aiId}/tables`, data);

// List all tables for an AI
export const listTables = (aiId: string, collectionId?: string) => 
  api.get(`/trainer/ais/${aiId}/tables${collectionId ? `?collectionId=${collectionId}` : ''}`);

// Delete a table
export const deleteTable = (aiId: string, tableId: string, deleteQA: boolean = true) => 
  api.delete(`/trainer/ais/${aiId}/tables/${tableId}?deleteQA=${deleteQA}`);

// Update a table (description or other properties)
export const updateTable = (aiId: string, tableId: string, data: { description?: string }) => 
  api.put(`/trainer/ais/${aiId}/tables/${tableId}`, data);

// Generate AI description for a table (supports both existing tables and CSV data)
export const generateTableDescription = (aiId: string, tableId: string, csvData?: any) => 
  api.post(`/trainer/ais/${aiId}/tables/${tableId}/generate-description`, csvData ? { csvData } : {});

// Sync table data from external source
export const syncTableData = (aiId: string, tableId: string) => 
  api.post(`/trainer/ais/${aiId}/tables/${tableId}/sync`);

// Processed URLs API functions
// Get all processed URLs for a specific AI and collection
export const getProcessedUrls = (aiId: string, collectionId: string) => 
  api.get(`/trainer/ais/${aiId}/collections/${collectionId}/processed-urls`);

// Add a new processed URL for a specific AI and collection
export const addProcessedUrl = (aiId: string, collectionId: string, data: { url: string; qaPairsCount: number }) => 
  api.post(`/trainer/ais/${aiId}/collections/${collectionId}/processed-urls`, data);

// Delete a processed URL
export const deleteProcessedUrl = (aiId: string, collectionId: string, urlId: string) => 
  api.delete(`/trainer/ais/${aiId}/collections/${collectionId}/processed-urls/${urlId}`);

// Downloaded Webpages API functions
// Download a single webpage and store content in Supabase
export const downloadWebpage = (aiId: string, collectionId: string, data: { url: string }) => 
  api.post(`/trainer/ais/${aiId}/collections/${collectionId}/download-webpage`, data);

// Get all downloaded webpages for an AI and collection
export const getDownloadedWebpages = (aiId: string, collectionId: string, options: { limit?: number; offset?: number; status?: string } = {}) => {
  const params = new URLSearchParams();
  if (options.limit) params.append('limit', options.limit.toString());
  if (options.offset) params.append('offset', options.offset.toString());
  if (options.status) params.append('status', options.status);
  
  const queryString = params.toString();
  return api.get(`/trainer/ais/${aiId}/collections/${collectionId}/downloaded-webpages${queryString ? `?${queryString}` : ''}`);
};

// Get a specific downloaded webpage by ID
export const getDownloadedWebpage = (aiId: string, collectionId: string, id: string) => 
  api.get(`/trainer/ais/${aiId}/collections/${collectionId}/downloaded-webpages/${id}`);

// Delete a downloaded webpage
export const deleteDownloadedWebpage = (aiId: string, collectionId: string, id: string) => 
  api.delete(`/trainer/ais/${aiId}/collections/${collectionId}/downloaded-webpages/${id}`);

// Update the status of a downloaded webpage
export const updateDownloadedWebpageStatus = (aiId: string, collectionId: string, id: string, status: string) => 
  api.put(`/trainer/ais/${aiId}/collections/${collectionId}/downloaded-webpages/${id}/status`, { status });

// Generate Q&A pairs from selected downloaded webpages
export const generateQAFromDownloaded = (aiId: string, collectionId: string, data: { downloadedWebpageIds: string[]; processImages?: boolean }) => 
  api.post(`/trainer/ais/${aiId}/collections/${collectionId}/generate-qa-from-downloaded`, data);

// Website Crawling API functions
// Start a new website crawling session
export const startCrawlSession = (aiId: string, collectionId: string, data: { startUrl: string; maxPages?: number; organizationContext?: string }) => 
  api.post(`/trainer/ais/${aiId}/collections/${collectionId}/start-crawl`, data);

// Get crawling progress for a session
export const getCrawlProgress = (aiId: string, collectionId: string, sessionId: string) => 
  api.get(`/trainer/ais/${aiId}/collections/${collectionId}/crawl-sessions/${sessionId}/progress`);

// Pause a crawling session
export const pauseCrawlSession = (aiId: string, collectionId: string, sessionId: string) => 
  api.post(`/trainer/ais/${aiId}/collections/${collectionId}/crawl-sessions/${sessionId}/pause`);

// Resume a paused crawling session
export const resumeCrawlSession = (aiId: string, collectionId: string, sessionId: string) => 
  api.post(`/trainer/ais/${aiId}/collections/${collectionId}/crawl-sessions/${sessionId}/resume`);

// Cancel a crawling session
export const cancelCrawlSession = (aiId: string, collectionId: string, sessionId: string) => 
  api.post(`/trainer/ais/${aiId}/collections/${collectionId}/crawl-sessions/${sessionId}/cancel`);

// Force cancel a crawling session (for cleanup/recovery)
export const forceCancelCrawlSession = (aiId: string, collectionId: string, sessionId: string) => 
  api.post(`/trainer/ais/${aiId}/collections/${collectionId}/crawl-sessions/${sessionId}/force-cancel`);

// Get all crawled pages for a session
export const getCrawlSessionResults = (aiId: string, collectionId: string, sessionId: string) => 
  api.get(`/trainer/ais/${aiId}/collections/${collectionId}/crawl-sessions/${sessionId}/results`);

// Get all crawl sessions for an AI and collection
export const getCrawlSessions = (aiId: string, collectionId: string, params?: { limit?: number; offset?: number; status?: string }) => 
  api.get(`/trainer/ais/${aiId}/collections/${collectionId}/crawl-sessions`, { params });

// Delete a crawl session and all its downloaded webpages
export const deleteCrawlSession = (aiId: string, collectionId: string, sessionId: string) => 
  api.delete(`/trainer/ais/${aiId}/collections/${collectionId}/crawl-sessions/${sessionId}`);

// Share-related API functions
// Create a share for an AI collection
export const createShare = (aiId: string, collectionId: string) => 
  api.post('/trainer/shares', { aiId, collectionId });

// Get share information for verification
export const getShareInfo = (shareCode: string, password: string) => 
  api.get('/trainer/shares/info', { params: { shareCode, password } });

// Import a shared AI
export const importSharedAi = (shareCode: string, password: string, newAiTitle?: string) => 
  api.post('/trainer/shares/import', { shareCode, password, newAiTitle });

// Get all shares created by the current user
export const getUserShares = () => 
  api.get('/trainer/shares');

// Delete/deactivate a share
export const deleteShare = (shareId: string) =>
  api.delete(`/trainer/shares/${shareId}`);

// Curtain Management API functions
// Reset curtains for the current collection and ensure schema
export const resetCurtainCollection = (aiId: string, collectionId: string) =>
  api.post(`/trainer/ais/${aiId}/collections/${collectionId}/reset-curtains`);

// Backward-compatible alias
export const migrateCurtainDatabase = (aiId: string, collectionId: string) =>
  resetCurtainCollection(aiId, collectionId);

// Get all curtains for a specific collection
export const getCurtainsForCollection = (aiId: string, collectionId: string) =>
  api.get(`/trainer/ais/${aiId}/collections/${collectionId}/curtains`);

// Create a new curtain for a collection
export const createCurtain = (aiId: string, collectionId: string, data: { identifier: string; image_data: string; description?: string }) =>
  api.post(`/trainer/ais/${aiId}/collections/${collectionId}/curtains`, data);

// Update an existing curtain
export const updateCurtain = (aiId: string, collectionId: string, curtainId: string, data: { identifier?: string; image_data?: string; description?: string; display_order?: number }) =>
  api.put(`/trainer/ais/${aiId}/collections/${collectionId}/curtains/${curtainId}`, data);

// Delete a curtain
export const deleteCurtain = (aiId: string, collectionId: string, curtainId: string) =>
  api.delete(`/trainer/ais/${aiId}/collections/${collectionId}/curtains/${curtainId}`);

export default api;
